/**
 * VIB-2546: validering av stegene i Nytt prosjekt-veiviseren som ren funksjon,
 * så hooken kan flytte fokus til første felt som feilet (ikke bare vise toast).
 * Reglene er uendret fra `useNewProjectForm.validateStep`.
 */
export type NewProjectField =
  | "customer"
  | "projectName"
  | "totalAmount"
  | "deadline"
  | "productionLead"
  | "invoiceTerms";

export interface NewProjectStepError {
  field: NewProjectField;
  message: string;
}

export interface NewProjectStepValues {
  isInternal: boolean;
  customerId: string;
  projectName: string;
  projectType: string;
  totalAmount: string;
  deadline: string;
  productionLeadId: string;
  invoiceTermsValid: boolean;
}

/** DOM-id per felt i StepBasicInfo. `null` = feltet har ikke ett fokuserbart element. */
export const NEW_PROJECT_FIELD_IDS: Record<NewProjectField, string | null> = {
  customer: "np-customer",
  projectName: "np-project-name",
  totalAmount: "np-total-amount",
  deadline: "np-deadline",
  productionLead: "np-production-lead",
  invoiceTerms: null,
};

export function validateNewProjectStep(
  step: number,
  v: NewProjectStepValues,
): NewProjectStepError[] {
  const errors: NewProjectStepError[] = [];
  if (step === 0) {
    // Internprosjekt har ikke kunde, kontraktssum eller deadline — det er
    // arbeid uten ekstern fakturering.
    if (!v.isInternal && !v.customerId) {
      errors.push({ field: "customer", message: "Kunde er obligatorisk" });
    }
    if (!v.projectName.trim()) {
      errors.push({ field: "projectName", message: "Prosjektnavn er obligatorisk" });
    }
    // Time-jobber har ingen kontraktssum — faktureres per medgått tid.
    if (
      !v.isInternal &&
      v.projectType !== "hourly" &&
      (!v.totalAmount || Number(v.totalAmount) <= 0)
    ) {
      errors.push({ field: "totalAmount", message: "Totalbeløp eks. mva er obligatorisk" });
    }
    if (!v.isInternal && !v.deadline) {
      errors.push({ field: "deadline", message: "Estimert deadline er obligatorisk" });
    }
    if (!v.productionLeadId) {
      errors.push({ field: "productionLead", message: "Prosjektleder er obligatorisk" });
    }
  }
  // Steg 1 (Prosjekteiere): salgseier er VALGFRI for alle prosjekter.
  if (step === 2 && !v.invoiceTermsValid) {
    // VIB-686: Faktureringsvilkår må summere til 100 %.
    errors.push({
      field: "invoiceTerms",
      message: "Faktureringsvilkårene må summere til 100 %. Juster terminene før du går videre.",
    });
  }
  return errors;
}

/** Flytter fokus til feltet som feilet. No-op når feltet ikke er montert. */
export function focusNewProjectField(field: NewProjectField): void {
  const id = NEW_PROJECT_FIELD_IDS[field];
  if (!id) return;
  document.getElementById(id)?.focus();
}
